import { Injectable } from '@angular/core';

import { Accessory, CartModel, Equipment, Fai, Plan, ProductFactory, Simo, Sowo } from '@bytel/bytel-sales';
import { CatalogRepository, CheckoutCartModel, ProductService } from '@bytel/checkout';

import { ConfigurationService } from '@services/configuration.service';

import { Observable, map, of } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';

import { ComparisonOperator, IWidgetRedirectRule } from '../interfaces/config.interface';

export const MIXED_CART_CATEGORY = 'FAI-SIMO';
export const BYOU_CATEGORY = 'fai-byou';

@Injectable({
    providedIn: 'root',
})
export class MixedCartService {
    private _isActive: boolean = false;
    private readonly MAX_OFFER_LENGTH: number = 3;

    constructor(
        private catalogRepository: CatalogRepository,
        private productService: ProductService,
        private configurationService: ConfigurationService,
    ) {
        this._isActive = this.configurationService.getData()?.global.mixedCartActive ?? false;
    }

    public getMixedCartOffers(
        category: string = MIXED_CART_CATEGORY,
        maxLength: number = this.MAX_OFFER_LENGTH,
    ): Observable<Plan[]> {
        return this.catalogRepository.getProductCategories([category], false, true).pipe(
            mergeMap((result) =>
                of(
                    result[0].products
                        .map((product) => ProductFactory.GetInstance(product))
                        .filter(
                            (offer) =>
                                (ProductFactory.Is(offer, Sowo) || ProductFactory.Is(offer, Simo)) && !offer.isPro,
                        ),
                ),
            ),
            map((offers) => offers.sort((a, b) => a.data.position - b.data.position).slice(0, maxLength) as Plan[]),
            catchError(() => of(null)),
        );
    }

    public canHaveMixedCart(cartModel: CheckoutCartModel): boolean {
        const quote = cartModel.getQuote();

        const plan = quote.getPrincipalProduct<Plan>('Plan');
        const equipment = quote.getPrincipalProduct<Equipment>('Equipment');
        const isSowo = ProductFactory.Is(plan, Sowo, true);
        const isFai = ProductFactory.Is(plan, Fai, true);
        const isSimo = ProductFactory.Is(plan, Simo, true);
        const isRenew = quote.isRenewal();

        const accessories = quote.getProductsByType<Accessory>('Accessory');

        const canMixedCartMobile = !equipment && (isSimo || isSowo);
        const canMixedCartFai = isFai;

        return (
            !cartModel.isBanking() && (canMixedCartMobile || canMixedCartFai) && !isRenew && accessories?.length === 0
        );
    }

    public isMixedCart(cartModel: CartModel): boolean {
        return cartModel?.quotes?.filter((quote) => quote.products.length > 0).length > 1;
    }

    public isActive(): boolean {
        return this._isActive;
    }

    public getActiveRule(offerName: string): IWidgetRedirectRule {
        const widgetConfig = this.configurationService.getData().global.widgets.faiWidget;
        return widgetConfig.redirect_rules
            ?.find((rule) => rule.offer_name === offerName)
            ?.attached_rules.find((r) => r.isActive);
    }

    public isCompatible(currentPlan: Plan, offerName: string): boolean {
        if (!currentPlan) {
            return false;
        }
        const activeRule = this.getActiveRule(offerName);
        return !!(activeRule && this.isRuleCompatible(currentPlan, activeRule));
    }

    public getRedirectUrl(currentPlan: Plan): { redirectUrl: string; focus?: string } {
        if (this.isCompatible(currentPlan, 'byou') && currentPlan?.data?.commercial_range === 'b_you') {
            const activeRule = this.getActiveRule('byou');
            return { redirectUrl: activeRule.redirect_url, focus: activeRule.focus };
        }

        if (this.isCompatible(currentPlan, 'panenka')) {
            const activeRule = this.getActiveRule('panenka');
            return { redirectUrl: activeRule.redirect_url, focus: activeRule.focus };
        }

        return { redirectUrl: '/offres-internet/test-eligibilite-panier-mixte' };
    }

    private isRuleCompatible(currentPlan: Plan, activeRule: IWidgetRedirectRule): boolean {
        switch (activeRule.operator) {
            case ComparisonOperator.EQUAL:
                return currentPlan.data[activeRule.property] === activeRule.value;
            case ComparisonOperator.NOT_EQUAL:
                return currentPlan.data[activeRule.property] !== activeRule.value;
            case ComparisonOperator.GREATER_THAN:
                return currentPlan.data[activeRule.property] > activeRule.value;
            case ComparisonOperator.GREATER_THAN_OR_EQUAL:
                return currentPlan.data[activeRule.property] >= activeRule.value;
            case ComparisonOperator.LESS_THAN:
                return currentPlan.data[activeRule.property] < activeRule.value;
            case ComparisonOperator.LESS_THAN_OR_EQUAL:
                return currentPlan.data[activeRule.property] <= activeRule.value;
            default:
                return false;
        }
    }
}
